<template>
  <div class="sid-select">
    <SidbearSettingsGroup
      :active="showActiveGroup"
      title="Font Styling"
    >
      <b-form-group v-if="linked">
        <button
          class="flex p-0 text-[11px] hover:bg-gray-100 items-center text-dark border rounded-[4px] w-[114px] h-[30px] justify-content-center gap-[5px] cursor-pointer font-[500] hover:text-[#3D82EA] text-[#7E7E7E]"
          @click="() => unlinkSid()"
        >
          Unlink Styles
          <i class="fa fa-link text-primary" />
        </button>
      </b-form-group>

      <editor-menu-bar
        v-if="editor"
        v-slot="{ commands }"
        :editor="editor"
      >
        <b-form-group v-if="section.component === 'ElRichText'">
          <div class="flex items-center justify-between">
            <label class="w-[210px] p-0">HTML Tag</label>
  
            <v-select
              :value="getBlockType()"
              :options="['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p']"
              :searchable="false"
              @input="val => setBlockType(val, commands)"
            />
          </div>
        </b-form-group>
      </editor-menu-bar>
      
      <template v-if="['ElHeadlineV2', 'ElSubheadlineV2'].includes(section.component)">
        <b-form-group>
          <div class="flex items-center">
            <label class="w-[210px] p-0">HTML Tag</label>
            <v-select
              v-model="headlineTag"
              :options="headlineTags"
              :reduce="val => val.value"
              :searchable="false"
            />
          </div>
        </b-form-group>

        <b-form-group>
          <div class="flex items-center">
            <label class="w-[90px] p-0">
              ID
              <i
                v-b-tooltip
                class="fa fa-info-circle cursor-pointer hover:opacity-80 ml-1"
                title="Generate an ID value for use with the Table of Contents 'Exclude Anchors' setting"
              />
            </label>
            
            <b-input-group>
              <b-input
                v-model="config.id"
              />
              <b-input-group-append>
                <b-input-group-text
                  class="bg-white cursor-pointer !border-[#E2E5EC]"
                  @click="config.id ? $refs.confirmDialog.open(null) : generateId()"
                >
                  <i class="fa fa-sync-alt text-[12px]" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
          </div>
        </b-form-group>
      </template>

      <b-form-group
        v-if="section.component === 'ElHeadline'"
        label="Headline tag"
      >
        <v-select
          v-model="headlineTag"
          :options="[{label: 'H1', value: 'h1'}, {label: 'H2', value: 'h2'}, {label: 'H3', value: 'h3'}]"
          :reduce="val => val.value"
          :searchable="false"
        />
      </b-form-group>

      <b-form-group
        v-if="section.component === 'ElTypography'"
      >
        <div class="flex items-center">
          <label class="w-[210px] p-0">HTML Tag</label>
          <v-select
            v-model="typographyTag"
            :options="typographyTags"
            :reduce="val => val.value"
            :searchable="false"
          />
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center justify-between">
          <label class="pb-0">Width Setting</label>

          <sid-select
            :sid="`.text-container-${section.sid}`"
            rule-name="display"
            :searchable="false"
            class="w-[138px]"
            :options="[
              {
                label: 'Block',
                value: '',
                customProperties: [[`.--${section.sid}--container`, ['display', '']], [`.--${section.sid}--container`, ['width', '']], [`.text-container-${section.sid}`, ['display', '']], [`.text-container-${section.sid}`, ['width', '']]]
              }, 
              {
                label: 'Inline', 
                value: 'inline-flex',
                customProperties: [[`.--${section.sid}--container`, ['display', 'inline-flex']], [`.--${section.sid}--container`, ['width', 'auto']], [`.text-container-${section.sid}`, ['display', 'inline-flex']], [`.text-container-${section.sid}`, ['width', 'auto']]]
              }
            ]"
          />
        </div>
      </b-form-group>

      <editor-menu-bar
        v-if="editor"
        v-slot="{ commands, getMarkAttrs}"
        :editor="editor"
      >
        <sid-font
          :sid="`.--${section.sid}-text div`"
          :editor="true"
          :selected-font="getMarkAttrs('fontfamily')"
          :selected-font-weight="getMarkAttrs('fontweight')"
          @onFontChange="font => applyFontFamily(font, commands, getMarkAttrs('fontfamily'))"
          @onFontWeightChange="weight => applyFontWeight(weight, commands, getMarkAttrs('fontweight'))"
          @onFontReset="() => resetFont(commands)"
        />
      </editor-menu-bar>

      <editor-menu-bar
        v-if="editor"
        v-slot="{commands, getMarkAttrs}"
        :editor="editor"
      >
        <sid-font-size
          label="Font Size"
          rule-name="font-size"
          :sid="`.--${section.sid}-text div`"
          :selected="getMarkAttrs('fontsize')"
          @onChange="val => applyFontSize(val, commands, getMarkAttrs('fontsize'))"
          @onReset="val => resetFontSize(val, commands, getMarkAttrs('fontsize'))"
        />
      </editor-menu-bar>

      <!--      <sid-range-slider-->
      <!--        label="Font Size"-->
      <!--        :sid="`.&#45;&#45;${section.sid}-text div`"-->
      <!--        default-dimension="px"-->
      <!--        :px-range="[0, 300]"-->
      <!--        rule-name="font-size"-->
      <!--      />-->

      <sid-range-slider
        label="Letter-Spacing"
        :sid="`.--${section.sid}-text div`"
        rule-name="letter-spacing"
        :min="-2"
        :dimensions="[]"
      />

      <sid-range-slider
        label="Line-Height"
        :sid="`.--${section.sid}-text div`"
        rule-name="line-height"
        :default-dimension="''"
        :min="0"
        :max="5"
        :step=".1"
        var="--line-height"
        :dimensions="[]"
      />

      <div class="position-relative">
        <color-picker-flat
          v-if="tipa && pickedColor"
          ref="colorMenuFlat"
          :color="getPickedColorAttributes(tipa.getMarkAttrs('textcolor'))"
          @input="event => applyTextColor(
            `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`,
            editor?.commands,
            null
          )"
        />
      </div>
      <div class="position-relative">
        <color-picker-flat
          v-if="backgroundColorTip"
          ref="backgroundColorMenuFlat"
          :color="getPickedBackgroundColorAttributes(backgroundColorTip.getMarkAttrs('backgroundColor'))"
          style="top: 440px; left: 0;"
          @input="event => applyBackgroundColor(`rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`, backgroundColorTip.commands, null)"
        />
      </div>
      <div class="position-relative">
        <color-picker-flat
          v-if="!!tableCellBgColor"
          ref="tableCellBgColor"
          color="white"
          style="top: 440px; left: 0;"
          @input="event => tableCellBgColor.command.setCellAttribute(tableCellBgColor.name, event.hex8)"
        />
      </div>
      <div class="position-relative">
        <color-picker-flat
          v-if="backgroundColorTip"
          ref="backgroundColorMenuFlat"
          :color="getPickedBackgroundColorAttributes(backgroundColorTip.getMarkAttrs('backgroundColor'))"
          style="top: 440px; left: 0;"
          @input="event => applyBackgroundColor(`rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`, backgroundColorTip.commands, null)"
        />
      </div>

      <editor-menu-bar
        v-if="editor"
        v-slot="{ commands, isActive, getMarkAttrs}"
        :editor="editor"
      >
        <div class="menubar">
          <b-form-group>
            <background-type
              :value="getPickedGradientColorAttributes(getMarkAttrs('textgradientcolor'))"
              :config="colorType"
              label="Color Type"
              @change="() => {
                const raw = getMarkAttrs('textgradientcolor').raw
                if (!raw) {
                  return
                }

                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
                if (Array.isArray(parsed) && parsed.length > 0) {
                  backgroundGradient.backgroundGradient = parsed
                }
              }"
              @updated="val => textColorTypeChanged(commands, getMarkAttrs, val)"
            />

            <div
              v-if="!colorType.backgroundType"
              class="flex justify-end"
            >
              <color-picker-placeholder
                ref="colorPickerInvoker"
                class="mr-2"
                :color="getPickedColorAttributes(getMarkAttrs('textcolor'))"
                @click.native="setCommands({commands, getMarkAttrs})"
              />

              <span
                class="mt-2 block text-[#333333] underline text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
                @click="() => applyTextColor('', commands)"
              >
                Reset color
              </span>
            </div>

            <div v-else>
              <gradient-background
                :config="backgroundGradient"
                direction-enabled
                @change="val => applyGradientTextColor(val, commands, getMarkAttrs('textgradientcolor').raw)"
              />
            </div>
          </b-form-group>

          <b-form-group v-if="!hide.includes('align')">
            <div class="flex items-center justify-between">
              <label class="p-0">Text Alignment</label>
              <sid-buttons-group
                class="mb-0"
                :options="[
                  {
                    svg: 'data:image/svg+xml,%3Csvg width=\'16\' height=\'16\' viewBox=\'0 0 16 16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'0.475586\' y=\'0.475464\' width=\'14.9508\' height=\'14.9508\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57178\' transform=\'scale(0.00195312)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57178\' width=\'512\' height=\'512\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
                    customProperties: [[`.--${section.sid}-text div`, ['text-align', 'left']], [`.--${section.sid}-text div p`, ['text-align', '']]],
                    value: 'left',
                    tooltip: 'Left'
                  },
                  {
                    svg: 'data:image/svg+xml,%3Csvg width=\'18\' height=\'14\' viewBox=\'0 0 18 14\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'0.951172\' y=\'0.431396\' width=\'16.5246\' height=\'13.0389\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57177\' transform=\'scale(0.00195312 0.00247525)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57177\' width=\'512\' height=\'404\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
                    customProperties: [[`.--${section.sid}-text div`, ['text-align', 'center']], [`.--${section.sid}-text div p`, ['text-align', '']]],
                    value: 'center',
                    tooltip: 'Center'
                  },
                  {
                    svg: 'data:image/svg+xml,%3Csvg width=\'15\' height=\'16\' viewBox=\'0 0 15 16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' xmlns:xlink=\'http://www.w3.org/1999/xlink\'%3E%3Crect x=\'14.9512\' y=\'15.4263\' width=\'14.9508\' height=\'14.9508\' transform=\'rotate(180 14.9512 15.4263)\' fill=\'url(%23pattern0)\'/%3E%3Cdefs%3E%3Cpattern id=\'pattern0\' patternContentUnits=\'objectBoundingBox\' width=\'1\' height=\'1\'%3E%3Cuse xlink:href=\'%23image0_7786_57179\' transform=\'scale(0.00195312)\'/%3E%3C/pattern%3E%3Cimage id=\'image0_7786_57179\' width=\'512\' height=\'512\' xlink:href=\'data:image/png;base64,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\'/%3E%3C/defs%3E%3C/svg%3E%0A',
                    customProperties: [[`.--${section.sid}-text div`, ['text-align', 'right']], [`.--${section.sid}-text div p`, ['text-align', '']]],
                    value: 'right',
                    tooltip: 'Right'
                  }
                ]"
                :sid="`.--${section.sid}-text div`"
                rule-name="text-align"
              />
            </div>
          </b-form-group>

          <b-form-group v-if="!hide.includes('vertical-align')">
            <div class="flex items-center justify-between">
              <label>Text Vertical Align</label>

              <b-btn-group>
                <b-btn
                  v-b-tooltip
                  title="Superscript"
                  variant="white"
                  class="h-[29px] w-[29px] p-0"
                  @click="() => editor.state.selection.empty ? $refs['vertical-align'].apply({value: 'super'}) : commands.verticalAlign({verticalAlign: 'super'})"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    :stroke="(getMarkAttrs('verticalAlign').verticalAlign || verticalAlign) === 'super'? '#000' : '#AEB1B4'"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="w-4 h-4"
                  >
                    <path d="m4 19 8-8" />
                    <path d="m12 19-8-8" />
                    <path d="M20 12h-4c0-1.5.442-2 1.5-2.5S20 8.334 20 7.002c0-.472-.17-.93-.484-1.29a2.105 2.105 0 0 0-2.617-.436c-.42.239-.738.614-.899 1.06" />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Baseline"
                  variant="white"
                  class="h-[29px] w-[29px] p-0"
                  @click="() => editor.state.selection.empty ? $refs['vertical-align'].apply({value: 'baseline'}) : commands.verticalAlign({verticalAlign: 'baseline'})"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    :stroke="(getMarkAttrs('verticalAlign').verticalAlign || verticalAlign) === 'baseline'? '#000' : '#AEB1B4'"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="w-4 h-4"
                  >
                    <path d="M4 16L12 8" />
                    <path d="M12 16L4 8" />
                    <path d="M20 14.9988H16C16 13.4988 16.442 12.9988 17.5 12.4988C18.558 11.9988 20 11.3328 20 10.0008C20 9.5288 19.83 9.0708 19.516 8.7108C19.196 8.34888 18.7613 8.10801 18.2848 8.02862C17.8083 7.94924 17.319 8.03616 16.899 8.27481C16.479 8.51381 16.161 8.88881 16 9.33481" />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Subscript"
                  variant="white"
                  class="h-[29px] w-[29px] p-0"
                  @click="() => editor.state.selection.empty ? $refs['vertical-align'].apply({value: 'sub'}) : commands.verticalAlign({verticalAlign: 'sub'})"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    :stroke="(getMarkAttrs('verticalAlign').verticalAlign || verticalAlign) === 'sub'? '#000' : '#AEB1B4'"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="w-4 h-4"
                  >
                    <path d="m4 5 8 8" />
                    <path d="m12 5-8 8" />
                    <path d="M20 19h-4c0-1.5.44-2 1.5-2.5S20 15.33 20 14c0-.47-.17-.93-.48-1.29a2.11 2.11 0 0 0-2.62-.44c-.42.24-.74.62-.9 1.07" />
                  </svg>
                </b-btn>
              </b-btn-group>
            </div>
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="p-0">Text Transform</label>

              <b-btn-group>
                <b-btn
                  v-b-tooltip
                  title="Uppercase"
                  variant="white"
                  class="h-[29px] w-[29px] p-0"
                  @click="() => editor.state.selection.empty ? $refs['text-transform'].apply({value: 'uppercase'}) : commands.textTransform({textTransform: 'uppercase'})"
                >
                  <svg
                    width="21"
                    height="12"
                    viewBox="0 0 21 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      d="M6.28906 8.22656L4.75781 4.52344H4.625L3.17969 8.22656H6.28906ZM0.289062 11.5938C0.674479 11.5677 0.984375 11.3932 1.21875 11.0703C1.36979 10.8672 1.58594 10.4193 1.86719 9.72656L5.42188 0.96875H5.86719L9.4375 9.375C9.83854 10.3177 10.138 10.9271 10.3359 11.2031C10.5339 11.474 10.8151 11.6042 11.1797 11.5938V12H6V11.5938C6.52083 11.5729 6.86198 11.5286 7.02344 11.4609C7.1901 11.3932 7.27344 11.2214 7.27344 10.9453C7.27344 10.8203 7.23177 10.6302 7.14844 10.375C7.09635 10.224 7.02604 10.0417 6.9375 9.82812L6.53125 8.85938H2.9375C2.70312 9.49479 2.55208 9.90885 2.48438 10.1016C2.34375 10.513 2.27344 10.8125 2.27344 11C2.27344 11.2292 2.42708 11.3958 2.73438 11.5C2.91667 11.5573 3.1901 11.5885 3.55469 11.5938V12H0.289062V11.5938Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'uppercase'? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M11.2578 11.5938C11.6901 11.5781 12 11.5208 12.1875 11.4219C12.5052 11.2552 12.6641 10.9375 12.6641 10.4688V2.72656C12.6641 2.25781 12.5156 1.94271 12.2188 1.78125C12.0417 1.68229 11.7214 1.61979 11.2578 1.59375V1.1875H16.2031C17.1562 1.1875 17.9479 1.29688 18.5781 1.51562C19.7656 1.92708 20.3594 2.67708 20.3594 3.76562C20.3594 4.42708 20.1172 4.95312 19.6328 5.34375C19.1536 5.72917 18.6328 5.97396 18.0703 6.07812V6.22656C18.6641 6.34115 19.1797 6.53385 19.6172 6.80469C20.4714 7.33594 20.8984 8.07552 20.8984 9.02344C20.8984 9.90885 20.4896 10.6276 19.6719 11.1797C18.8542 11.7266 17.7656 12 16.4062 12H11.2578V11.5938ZM15.2188 6.03906C16.2083 6.03906 16.8724 5.8776 17.2109 5.55469C17.5495 5.23177 17.7188 4.65885 17.7188 3.83594C17.7188 3.22656 17.6068 2.71354 17.3828 2.29688C17.1641 1.88021 16.7396 1.67188 16.1094 1.67188C15.776 1.67188 15.5443 1.72917 15.4141 1.84375C15.2839 1.95312 15.2188 2.15365 15.2188 2.44531V6.03906ZM15.2188 10.5234C15.224 10.8047 15.2656 11.0104 15.3438 11.1406C15.474 11.3698 15.724 11.4844 16.0938 11.4844C16.849 11.4844 17.3776 11.2786 17.6797 10.8672C17.987 10.4557 18.1406 9.86979 18.1406 9.10938C18.1406 7.97396 17.8047 7.21875 17.1328 6.84375C16.7266 6.61458 16.0885 6.50781 15.2188 6.52344V10.5234Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'uppercase'? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Capitalize"
                  class="h-[29px] w-[29px] p-0"
                  variant="white"
                  @click="() => editor.state.selection.empty ? $refs['text-transform'].apply({value: 'capitalize'}) : commands.textTransform({textTransform: 'capitalize'})"
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      d="M6.28906 10.2266L4.75781 6.52344H4.625L3.17969 10.2266H6.28906ZM0.289062 13.5938C0.674479 13.5677 0.984375 13.3932 1.21875 13.0703C1.36979 12.8672 1.58594 12.4193 1.86719 11.7266L5.42188 2.96875H5.86719L9.4375 11.375C9.83854 12.3177 10.138 12.9271 10.3359 13.2031C10.5339 13.474 10.8151 13.6042 11.1797 13.5938V14H6V13.5938C6.52083 13.5729 6.86198 13.5286 7.02344 13.4609C7.1901 13.3932 7.27344 13.2214 7.27344 12.9453C7.27344 12.8203 7.23177 12.6302 7.14844 12.375C7.09635 12.224 7.02604 12.0417 6.9375 11.8281L6.53125 10.8594H2.9375C2.70312 11.4948 2.55208 11.9089 2.48438 12.1016C2.34375 12.513 2.27344 12.8125 2.27344 13C2.27344 13.2292 2.42708 13.3958 2.73438 13.5C2.91667 13.5573 3.1901 13.5885 3.55469 13.5938V14H0.289062V13.5938Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'capitalize' ? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M14.3672 12.4453C14.388 12.737 14.4453 12.9766 14.5391 13.1641C14.7266 13.5286 15.0391 13.7109 15.4766 13.7109C16.0234 13.7109 16.4089 13.4219 16.6328 12.8438C16.862 12.2656 16.9766 11.4479 16.9766 10.3906C16.9766 9.55208 16.875 8.83073 16.6719 8.22656C16.474 7.6224 16.0833 7.32031 15.5 7.32031C15.1562 7.32031 14.8802 7.44792 14.6719 7.70312C14.4688 7.95312 14.3672 8.13281 14.3672 8.24219V12.4453ZM14.3672 3.1875V7.33594C14.6172 7.09115 14.849 6.90625 15.0625 6.78125C15.4531 6.55729 15.875 6.44531 16.3281 6.44531C17.2292 6.44531 17.9531 6.79688 18.5 7.5C19.0521 8.20312 19.3281 9.06771 19.3281 10.0938C19.3281 11.4167 18.9766 12.4375 18.2734 13.1562C17.5755 13.8698 16.7057 14.2266 15.6641 14.2266C15.237 14.2266 14.8594 14.1432 14.5312 13.9766C14.2031 13.8099 13.8958 13.5911 13.6094 13.3203L12.3516 14.2266H12.125V4.5C12.125 4.16667 12.0651 3.94271 11.9453 3.82812C11.8255 3.70833 11.6016 3.625 11.2734 3.57812V3.1875H14.3672Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'capitalize' ? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Lowercase"
                  class="h-[29px] w-[29px] p-0"
                  variant="white"
                  @click="() => editor.state.selection.empty ? $refs['text-transform'].apply({value: 'lowercase'}) : commands.textTransform({textTransform: 'lowercase'})"
                >
                  <svg
                    width="17"
                    height="12"
                    viewBox="0 0 17 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      d="M3.50781 10.0859C3.76823 10.0859 4.01042 10.0078 4.23438 9.85156C4.35938 9.75781 4.50781 9.61198 4.67969 9.41406V7.07812C4.2526 7.18229 3.8776 7.34635 3.55469 7.57031C3.00781 7.95052 2.73438 8.4349 2.73438 9.02344C2.73438 9.3724 2.8125 9.63802 2.96875 9.82031C3.13021 9.9974 3.3099 10.0859 3.50781 10.0859ZM0.40625 9.50781C0.40625 8.78906 0.752604 8.21875 1.44531 7.79688C2.13802 7.375 3.22396 6.95052 4.70312 6.52344V5.35938C4.70312 4.88542 4.60156 4.53646 4.39844 4.3125C4.20052 4.08333 3.84635 3.96875 3.33594 3.96875C3.04948 3.96875 2.8125 4.02344 2.625 4.13281C2.44271 4.23698 2.35156 4.36979 2.35156 4.53125C2.35156 4.59375 2.36198 4.64583 2.38281 4.6875C2.40365 4.72917 2.4349 4.77344 2.47656 4.82031L2.57031 4.92969C2.64323 5.01823 2.70052 5.11719 2.74219 5.22656C2.78906 5.33073 2.8125 5.44271 2.8125 5.5625C2.8125 5.86458 2.71094 6.09896 2.50781 6.26562C2.3099 6.42708 2.07812 6.50781 1.8125 6.50781C1.50521 6.50781 1.24219 6.41667 1.02344 6.23438C0.809896 6.05208 0.703125 5.78906 0.703125 5.44531C0.703125 4.83073 0.992188 4.34375 1.57031 3.98438C2.14844 3.625 2.85938 3.44531 3.70312 3.44531C4.60938 3.44531 5.36719 3.61979 5.97656 3.96875C6.58073 4.32292 6.88281 4.97396 6.88281 5.92188V9.79688C6.88281 9.91146 6.91146 10.0104 6.96875 10.0938C7.03125 10.1719 7.09896 10.2109 7.17188 10.2109C7.23438 10.2109 7.28906 10.2005 7.33594 10.1797C7.38802 10.1536 7.46615 10.0885 7.57031 9.98438L7.8125 10.3203C7.5 10.6849 7.15365 10.9401 6.77344 11.0859C6.53385 11.1797 6.28125 11.2266 6.01562 11.2266C5.54167 11.2266 5.19531 11.0885 4.97656 10.8125C4.85156 10.6562 4.76042 10.4245 4.70312 10.1172C4.26042 10.5599 3.76042 10.8724 3.20312 11.0547C2.86458 11.1693 2.53125 11.2266 2.20312 11.2266C1.76562 11.2266 1.35677 11.0885 0.976562 10.8125C0.596354 10.5312 0.40625 10.0964 0.40625 9.50781Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'lowercase' ? '#000' : '#AEB1B4'"
                    />
                    <path
                      d="M11.3672 9.44531C11.388 9.73698 11.4453 9.97656 11.5391 10.1641C11.7266 10.5286 12.0391 10.7109 12.4766 10.7109C13.0234 10.7109 13.4089 10.4219 13.6328 9.84375C13.862 9.26562 13.9766 8.44792 13.9766 7.39062C13.9766 6.55208 13.875 5.83073 13.6719 5.22656C13.474 4.6224 13.0833 4.32031 12.5 4.32031C12.1562 4.32031 11.8802 4.44792 11.6719 4.70312C11.4688 4.95312 11.3672 5.13281 11.3672 5.24219V9.44531ZM11.3672 0.1875V4.33594C11.6172 4.09115 11.849 3.90625 12.0625 3.78125C12.4531 3.55729 12.875 3.44531 13.3281 3.44531C14.2292 3.44531 14.9531 3.79688 15.5 4.5C16.0521 5.20312 16.3281 6.06771 16.3281 7.09375C16.3281 8.41667 15.9766 9.4375 15.2734 10.1562C14.5755 10.8698 13.7057 11.2266 12.6641 11.2266C12.237 11.2266 11.8594 11.1432 11.5312 10.9766C11.2031 10.8099 10.8958 10.5911 10.6094 10.3203L9.35156 11.2266H9.125V1.5C9.125 1.16667 9.0651 0.942708 8.94531 0.828125C8.82552 0.708333 8.60156 0.625 8.27344 0.578125V0.1875H11.3672Z"
                      :fill="(getMarkAttrs('textTransform').textTransform || textTransform) === 'lowercase' ? '#000' : '#AEB1B4'"
                    />
                  </svg>
                </b-btn>
              </b-btn-group>
            </div>
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="p-0">Text Decoration</label>

              <b-btn-group>
                <b-btn
                  v-b-tooltip
                  title="Bold"
                  :class="{ 'is-active': isActive.bold() }"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.bold"
                >
                  <svg
                    fill="none"
                    height="14"
                    viewBox="0 0 13 14"
                    width="13"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      :fill="isActive.bold() ? '#000' : '#AEB1B4'"
                      d="M11.9766 7.47738C11.4408 6.87067 10.6462 6.44901 9.59244 6.21208C10.3309 5.88273 10.7774 5.66032 10.9321 5.54481C11.3072 5.27323 11.5899 4.96983 11.7801 4.63476C11.9706 4.29958 12.0658 3.92982 12.0658 3.52543C12.0658 3.14991 12.0034 2.79748 11.8783 2.46813C11.7533 2.13871 11.5598 1.83829 11.2979 1.56676C11.0301 1.29529 10.7264 1.07561 10.3871 0.908051C10.0716 0.757884 9.78582 0.645235 9.52983 0.570136C8.91673 0.41411 8.3482 0.336097 7.8244 0.336097H7.1636C7.04457 0.336097 6.92384 0.33464 6.80184 0.331726C6.67992 0.328994 6.61593 0.327477 6.60999 0.327477C6.58031 0.327477 6.53856 0.328994 6.48483 0.331757C6.43125 0.334671 6.38947 0.336128 6.35975 0.336128L5.95786 0.34484L2.56479 0.457488L0.243197 0.509517L0.278914 1.22878C0.778887 1.29241 1.11835 1.33008 1.29688 1.34155C1.60047 1.35889 1.80586 1.40233 1.91304 1.47157C1.97859 1.51786 2.01431 1.55249 2.02025 1.57559C2.07974 1.70269 2.11239 2.01757 2.11846 2.52028C2.14223 3.37542 2.16909 4.54251 2.19877 6.02157L2.21663 10.3289C2.21663 11.0683 2.18989 11.6289 2.13632 12.0102C2.11251 12.1489 2.04993 12.2962 1.94875 12.4522C1.6749 12.5619 1.30882 12.6516 0.850446 12.7209C0.713615 12.7381 0.511167 12.7728 0.243322 12.825L0.225464 13.6396C1.64816 13.5933 2.45774 13.5616 2.65443 13.5442C3.92826 13.469 4.81536 13.4373 5.31536 13.4488L7.07437 13.4833C7.76516 13.5064 8.35446 13.4746 8.84251 13.3881C9.61634 13.2494 10.2204 13.0759 10.6552 12.8679C11.0956 12.66 11.5123 12.3479 11.9051 11.932C12.2031 11.6143 12.4141 11.2763 12.5391 10.9179C12.7118 10.4269 12.798 9.9619 12.798 9.52269C12.7982 8.77173 12.5244 8.08986 11.9766 7.47738ZM4.98497 1.33302C5.44929 1.25793 5.83623 1.22038 6.14577 1.22038C7.16366 1.22038 7.92285 1.43708 8.42279 1.87037C8.92849 2.3036 9.18163 2.8439 9.18163 3.49095C9.18163 4.40968 8.91682 5.05676 8.38704 5.43229C7.85727 5.80778 7.07143 5.99562 6.0297 5.99562C5.63682 5.99562 5.31239 5.97543 5.05646 5.935C5.05043 5.73851 5.04746 5.5161 5.04746 5.26761L5.05646 4.4183C5.06234 3.51129 5.04449 2.70532 5.00286 2.00032C4.99091 1.80969 4.98497 1.5874 4.98497 1.33302ZM9.44973 11.3428C9.22946 11.7587 8.86622 12.0852 8.3604 12.3222C7.85433 12.5591 7.21133 12.6774 6.43159 12.6774C6.05059 12.6774 5.63388 12.5852 5.18147 12.4003C5.11004 12.2328 5.07429 12.1055 5.07417 12.0188L5.04746 9.6788L5.05646 8.1795V6.93147C5.21722 6.87371 5.51788 6.84487 5.95836 6.84487C6.95252 6.84487 7.68763 6.93733 8.16389 7.12216C8.65796 7.30721 9.07771 7.67117 9.42299 8.21429C9.66703 8.59573 9.78904 9.11565 9.78904 9.77436C9.78892 10.4155 9.67576 10.9384 9.44973 11.3428Z"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Italic"
                  :class="{ 'is-active': isActive.italic() }"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.italic"
                >
                  <svg
                    fill="none"
                    height="14"
                    viewBox="0 0 10 14"
                    width="10"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      :fill="isActive.italic() ? '#000' : '#AEB1B4'"
                      d="M5.65722 10.7081C5.68453 10.6038 5.71183 10.4968 5.73557 10.3884C5.73915 10.3708 6.15226 8.55152 6.59386 6.65231C7.3536 3.38626 7.53879 2.72926 7.58034 2.62902C7.7216 2.29577 7.95071 2.04381 8.28072 1.85822C8.56563 1.69838 8.92888 1.59271 9.36098 1.54395C9.488 1.52905 9.6079 1.44912 9.69575 1.31908C9.77528 1.20122 9.82158 1.05221 9.82158 0.909973C9.82158 0.588923 9.59247 0.327477 9.31113 0.327477H3.52402C3.24268 0.327477 3.01357 0.588923 3.01357 0.909973V0.930293C3.01357 1.21748 3.194 1.4586 3.44212 1.50466C3.77094 1.56562 4.22203 1.64961 4.5758 1.84061C4.75743 1.93815 4.89868 2.05736 4.99247 2.19282C5.09931 2.3459 5.15154 2.52335 5.15154 2.73332C5.15154 3.12617 5.05064 3.52037 4.9533 3.90103C4.93312 3.98095 4.91175 4.06494 4.89157 4.14622C4.76692 4.65421 4.58292 5.425 4.37043 6.31771C3.88016 8.3727 3.27 10.9289 3.11686 11.4152C3.01358 11.7431 2.63014 12.5193 1.31128 12.6344C1.18307 12.6453 1.06317 12.7116 0.976516 12.8187C0.88867 12.927 0.841187 13.0666 0.841187 13.2142V13.2156C0.841187 13.5366 1.0703 13.7981 1.35164 13.7981H7.13995C7.42128 13.7981 7.6504 13.5366 7.6504 13.2156V13.2115C7.6504 12.9175 7.45809 12.6696 7.20286 12.6344C7.153 12.6276 7.10433 12.6209 7.0521 12.6141C6.54402 12.5477 5.91249 12.4637 5.64658 12.0695C5.52313 11.8866 5.48395 11.6455 5.5243 11.3353C5.55038 11.1254 5.60263 10.9222 5.65722 10.7081Z"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Underline"
                  :class="{ 'is-active': isActive.underline() }"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.underline"
                >
                  <svg
                    fill="none"
                    height="16"
                    viewBox="0 0 15 16"
                    width="15"
                    xmlns="http://www.w3.org/2000/svg"
                    class="scale-75"
                  >
                    <path
                      :fill="isActive.underline() ? '#000' : '#AEB1B4'"
                      d="M0.517612 1.31839C1.27851 1.33713 1.73685 1.45882 1.89265 1.6828C1.99861 1.8389 2.05169 2.50295 2.05169 3.67535V6.79053C2.05169 7.76975 2.10149 8.50867 2.20126 9.00761C2.33861 9.74968 2.59739 10.3579 2.97779 10.8317C3.35183 11.2995 3.91011 11.6801 4.65216 11.9731C5.38184 12.2599 6.2674 12.4033 7.3088 12.4033C8.21923 12.4033 9.01447 12.3006 9.6944 12.0947C10.3615 11.8951 10.9291 11.6175 11.3967 11.2621C11.8584 10.9005 12.2076 10.5169 12.4444 10.1114C12.6065 9.82461 12.7346 9.44405 12.8281 8.97043C12.9591 8.29071 13.006 7.34243 12.9685 6.12641C12.8813 3.77515 12.8126 2.5404 12.7626 2.42189C12.7626 2.35338 12.7579 2.25669 12.7487 2.13198C12.7393 2.00718 12.7346 1.92314 12.7346 1.87943C12.7346 1.71727 12.7815 1.58933 12.875 1.49588C12.9126 1.45849 13.1587 1.40538 13.614 1.33677C13.8573 1.33055 14.1193 1.28988 14.3999 1.21518C14.425 1.06554 14.4372 0.968885 14.4372 0.925143C14.4372 0.844082 14.4188 0.685008 14.3813 0.448148L14.2129 0.42944C13.4644 0.510533 12.8533 0.541627 12.3792 0.522951L10.4617 0.42944H9.67592L9.65708 1.23402L9.78814 1.262L10.7236 1.24333C10.9916 1.2371 11.2318 1.34316 11.4438 1.56148C11.5622 1.69244 11.6372 1.96675 11.6683 2.3846L11.7055 2.93666C11.7432 3.47296 11.7852 3.97024 11.832 4.42856C11.8789 4.88694 11.9132 5.26895 11.935 5.57445C11.9567 5.88012 11.9677 6.27923 11.9677 6.77192C11.9677 7.74485 11.9022 8.45867 11.7712 8.91391C11.6654 9.27563 11.4999 9.6313 11.2756 9.98048C11.1693 10.1551 10.9667 10.3547 10.6673 10.5793C10.3993 10.785 10.0907 10.944 9.74132 11.0562C9.19864 11.2309 8.60314 11.3181 7.95469 11.3181C7.39946 11.3181 6.8478 11.1717 6.29892 10.8784C5.91852 10.6727 5.61922 10.3859 5.40094 10.0179C5.12037 9.53159 4.9613 8.90146 4.92385 8.1281L4.79289 5.50892L4.78335 3.36693C4.78335 3.32965 4.78175 3.25009 4.77864 3.1285C4.77549 3.00691 4.77392 2.90576 4.77392 2.82457C4.77392 2.0887 4.81445 1.67717 4.89548 1.58982C5.01412 1.43385 5.26038 1.35591 5.63455 1.35591C6.03379 1.35591 6.42038 1.32789 6.79455 1.27177V1.18756L6.77588 0.588907L6.78515 0.45788C6.59813 0.47033 6.33002 0.476621 5.98074 0.476621C5.79372 0.482847 5.33839 0.498443 4.61498 0.523377C4.10345 0.542119 3.57964 0.551359 3.04335 0.551359C2.83126 0.551359 2.31368 0.529668 1.49046 0.485927C1.16619 0.461025 0.816918 0.448607 0.442744 0.448607C0.274333 0.448607 0.149598 0.451622 0.0686035 0.457912L0.0966829 1.28117C0.146518 1.29349 0.28685 1.30601 0.517612 1.31839Z"
                    />
                    <path
                      :fill="isActive.underline() ? '#000' : '#AEB1B4'"
                      d="M14.138 13.9998H0.367917C0.28063 13.9998 0.208873 14.0395 0.152746 14.1191C0.0965526 14.1987 0.0686035 14.3002 0.0686035 14.4239V15.2721C0.0686035 15.3959 0.0966836 15.4975 0.152746 15.577C0.208873 15.6566 0.280499 15.6961 0.367917 15.6961H14.138C14.2254 15.6961 14.2972 15.6565 14.3533 15.577C14.4093 15.4975 14.4372 15.3959 14.4372 15.2721V14.4239C14.4372 14.3002 14.4093 14.1986 14.3533 14.1191C14.2972 14.0395 14.2254 13.9998 14.138 13.9998Z"
                    />
                  </svg>
                </b-btn>
                <b-btn
                  v-b-tooltip
                  title="Strikethrough"
                  :class="{ 'is-active': isActive.strike() }"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.strike"
                >
                  <i
                    :class="isActive.strike() ? 'text-dark' : 'text-[#AEB1B4]'"
                    class="fa fa-strikethrough text-[12px]"
                  />
                </b-btn>
              </b-btn-group>
            </div>
          </b-form-group>

          <b-form-group>
            <div class="flex items-center justify-between">
              <label class="p-0">List</label>

              <b-btn-group>
                <b-btn
                  v-b-tooltip
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  :class="{ 'is-active': isActive.bullet_list() }"
                  title="Bullet List"
                  @click="commands.bullet_list"
                >
                  <i
                    :class="isActive.bullet_list() ? 'text-dark' : 'text-[#AEB1B4]'"
                    class="fa fa-list-ul"
                  />
                </b-btn>

                <b-btn
                  v-b-tooltip
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  :class="{ 'is-active': isActive.ordered_list() }"
                  title="Ordered List"
                  @click="commands.ordered_list"
                >
                  <i
                    :class="isActive.ordered_list() ? 'text-dark' : 'text-[#AEB1B4]'"
                    class="fa fa-list-ol"
                  />
                </b-btn>
              </b-btn-group>
            </div>
          </b-form-group>

          <b-form-group>
            <div class="d-flex align-items-center justify-between sid-select">
              <label class="mb-0 mt-2">Text Link</label>

              <div>
                <template v-if="linkMenuIsActive">
                  <b-btn
                    class="text-danger"
                    variant="link"
                    @click="deleteLinkUrl(commands)"
                  >
                    <i class="fa fa-trash" />
                  </b-btn>
                </template>

                <b-btn
                  :class="{ 'is-active': isActive.link() || linkMenuIsActive }"
                  class="h-[29px] w-[29px] flex items-center justify-center p-0"
                  variant="white"
                  @click="linkMenuIsActive = !linkMenuIsActive"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M1.875 9.99996C1.875 7.81383 3.64721 6.04163 5.83333 6.04163H8.33333C8.6785 6.04163 8.95833 6.32145 8.95833 6.66663C8.95833 7.0118 8.6785 7.29163 8.33333 7.29163H5.83333C4.33756 7.29163 3.125 8.50421 3.125 9.99996C3.125 11.4957 4.33756 12.7083 5.83333 12.7083H8.33333C8.6785 12.7083 8.95833 12.9881 8.95833 13.3333C8.95833 13.6785 8.6785 13.9583 8.33333 13.9583H5.83333C3.64721 13.9583 1.875 12.1861 1.875 9.99996Z"
                      fill="#717171"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11.042 6.66663C11.042 6.32145 11.3218 6.04163 11.667 6.04163H14.167C16.3532 6.04163 18.1253 7.81383 18.1253 9.99996C18.1253 12.1861 16.3532 13.9583 14.167 13.9583H11.667C11.3218 13.9583 11.042 13.6785 11.042 13.3333C11.042 12.9881 11.3218 12.7083 11.667 12.7083H14.167C15.6627 12.7083 16.8753 11.4957 16.8753 9.99996C16.8753 8.50421 15.6627 7.29163 14.167 7.29163H11.667C11.3218 7.29163 11.042 7.0118 11.042 6.66663Z"
                      fill="#717171"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M6.875 10C6.875 9.65483 7.15483 9.375 7.5 9.375H12.5C12.8452 9.375 13.125 9.65483 13.125 10C13.125 10.3452 12.8452 10.625 12.5 10.625H7.5C7.15483 10.625 6.875 10.3452 6.875 10Z"
                      fill="#717171"
                    />
                  </svg>
                </b-btn>
              </div>
            </div>

            <template v-if="linkMenuIsActive">
              <tiptap-link
                :value="getLinkAttributes(getMarkAttrs('link'))"
                class="mt-2"
                :sid="`.--${section.sid}-text`"
                @change="link => linkAttrs = link"
              >
                <b-btn
                  block
                  variant="primary"
                  @click="setLinkUrl(commands.link, linkAttrs)"
                >
                  {{ linkSaveText }}
                </b-btn>
              </tiptap-link>
            </template>
          </b-form-group>

          <b-form-group>
            <div class="d-flex items-center justify-between sid-select">
              <label class="mb-0 mt-2">Global Variable</label>
              
              <div>
                <template v-if="globalVariableActive">
                  <b-btn
                    class="text-danger"
                    variant="link"
                    @click="() => {
                      commands.globalVariable({ remove: true })
                      globalVariableActive = false
                    }"
                  >
                    <i class="fa fa-trash" />
                  </b-btn>
                </template>
                
                <b-btn
                  :class="{ 'is-active': globalVariableActive }"
                  class="h-[29px] w-[29px] flex items-center justify-center p-0"
                  variant="white"
                  @click="globalVariableActive = !globalVariableActive"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-variable-icon lucide-variable w-4 h-4"
                  >
                    <path d="M8 21s-4-3-4-9 4-9 4-9" />
                    <path d="M16 3s4 3 4 9-4 9-4 9" />
                    <line
                      x1="15"
                      x2="9"
                      y1="9"
                      y2="15"
                    />
                    <line
                      x1="9"
                      x2="15"
                      y1="9"
                      y2="15"
                    />
                  </svg>
                </b-btn>
              </div>
            </div>

            <template v-if="globalVariableActive">
              <global-variable-content
                class="mt-3"
                :selected="getMarkAttrs('globalVariable')"
                @change="applyGlobalVariable(commands, $event)"
              />
              
              <global-variable-link
                class="mt-3"
                :selected="getMarkAttrs('globalVariableLink')"
                @change="applyGlobalVariableLink(commands, $event)"
              />
            </template>
          </b-form-group>

          <b-form-group
            class="!mb-[17px]"
            label="Background effect"
          >
            <v-select
              v-model="bgEffect"
              :options="['Select effect', 'Background color', 'Background mark']"
              :searchable="false"
            />
          </b-form-group>

          <b-form-group v-if="bgEffect === 'Background mark' || isActive.background()">
            <tiptap-background-color
              :commands="commands"
              :config="backgroundMark"
              :is-active="isActive.background()"
              :marks="() => getMarkAttrs('background')"
            />
          </b-form-group>

          <b-form-group
            v-else-if="bgEffect === 'Background color' || isActive.backgroundColor()"
          >
            <div class="flex items-center gap-[10px]">
              <color-picker-placeholder
                ref="backgroundColorPickerInvoker"
                :color="getPickedBackgroundColorAttributes(getMarkAttrs('backgroundColor'))"
                class="mb-2"
                @click.native="setBgCommands({commands, getMarkAttrs})"
              />
              <label class="mb-[5px] pb-0">Color</label>
            </div>

            <b-link
              v-if="isActive.backgroundColor()"
              @click="commands.backgroundColor(false)"
            >
              Remove color
            </b-link>
          </b-form-group>

          <div class="position-relative">
            <color-picker-flat
              v-if="backgroundMark.color"
              ref="backgroundMarkColorMenuFlat"
              :color="backgroundMark.color"
              style="top: -20px;left:0;"
              @input="event => backgroundMark.color = event.hex8"
            />
          </div>

          <b-form-group label="Table">
            <b-btn-group>
              <template v-if="isActive.table()">
                <b-btn
                  v-b-tooltip
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  title="Remove Table"
                  @click="commands.deleteTable"
                >
                  <svg
                    width="15"
                    height="17"
                    viewBox="0 0 15 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="12"
                      height="12"
                      rx="1.5"
                      stroke="#AFB1B4"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="11"
                      height="2"
                      fill="#AFB1B4"
                    />
                    <rect
                      x="1"
                      y="7"
                      width="11"
                      height="1"
                      fill="#AFB1B4"
                    />
                    <rect
                      x="6"
                      y="13"
                      width="11"
                      height="1"
                      transform="rotate(-90 6 13)"
                      fill="#AFB1B4"
                    />
                    <ellipse
                      cx="11"
                      cy="13"
                      rx="4"
                      ry="4"
                      transform="rotate(-90 11 13)"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M8 14L8 12L14 12L14 14L8 14Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Prepend Column"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.addColumnBefore"
                >
                  <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      stroke="#AFB1B4"
                    />
                    <circle
                      cx="10"
                      cy="9"
                      r="4"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M7 8H13V10H7V8Z"
                      fill="white"
                    />
                    <path
                      d="M9 6H11V12H9V6Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Append Column"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.addColumnAfter"
                >
                  <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="9.5"
                      y="0.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      stroke="#AFB1B4"
                    />
                    <circle
                      cx="4"
                      cy="9"
                      r="4"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M1 8H7V10H1V8Z"
                      fill="white"
                    />
                    <path
                      d="M3 6H5V12H3V6Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Remove Column"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.deleteColumn"
                >
                  <svg
                    width="10"
                    height="14"
                    viewBox="0 0 10 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="5.5"
                      y="0.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      stroke="#AFB1B4"
                    />
                    <circle
                      cx="4"
                      cy="10"
                      r="4"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M1 9H7V11H1V9Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Prepend Row"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.addRowBefore"
                >
                  <svg
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="4.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      transform="rotate(-90 0.5 4.5)"
                      stroke="#AFB1B4"
                    />
                    <circle
                      cx="9"
                      cy="10"
                      r="4"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M6 9H12V11H6V9Z"
                      fill="white"
                    />
                    <path
                      d="M8 7H10V13H8V7Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Append Row"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.addRowAfter"
                >
                  <svg
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="12.5"
                      y="9.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      transform="rotate(90 12.5 9.5)"
                      stroke="#AFB1B4"
                    />
                    <circle
                      cx="9"
                      cy="4"
                      r="4"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M6 3H12V5H6V3Z"
                      fill="white"
                    />
                    <path
                      d="M8 1H10V7H8V1Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Remove Row"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.deleteRow"
                >
                  <svg
                    width="15"
                    height="11"
                    viewBox="0 0 15 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="4.5"
                      width="4"
                      height="12"
                      rx="1.5"
                      transform="rotate(-90 0.5 4.5)"
                      stroke="#AFB1B4"
                    />
                    <ellipse
                      cx="11"
                      cy="7"
                      rx="4"
                      ry="4"
                      transform="rotate(-90 11 7)"
                      fill="#AFB1B4"
                    />
                    <path
                      d="M8 8L8 6L14 6L14 8L8 8Z"
                      fill="white"
                    />
                  </svg>
                </b-btn>

                <b-btn
                  v-b-tooltip
                  title="Cell Merge"
                  variant="white"
                  class="h-[28px] w-[28px] p-0"
                  @click="commands.toggleCellMerge"
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="12"
                      height="12"
                      rx="1.5"
                      stroke="#AFB1B4"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="11"
                      height="2"
                      fill="#AFB1B4"
                    />
                    <rect
                      x="6"
                      y="5"
                      width="3"
                      height="1"
                      transform="rotate(-90 6 5)"
                      fill="#AFB1B4"
                    />
                    <rect
                      x="6"
                      y="9"
                      width="3"
                      height="1"
                      transform="rotate(-90 6 9)"
                      fill="#AFB1B4"
                    />
                    <rect
                      x="6"
                      y="13"
                      width="3"
                      height="1"
                      transform="rotate(-90 6 13)"
                      fill="#AFB1B4"
                    />
                  </svg>
                </b-btn>
              </template>

              <b-btn
                v-else
                v-b-tooltip
                variant="white"
                class="h-[28px] w-[28px] p-0"
                title="Add Table"
                @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="12"
                    height="12"
                    rx="1.5"
                    stroke="#AFB1B4"
                  />
                  <rect
                    x="1"
                    y="1"
                    width="11"
                    height="2"
                    fill="#AFB1B4"
                  />
                  <rect
                    x="1"
                    y="7"
                    width="11"
                    height="1"
                    fill="#AFB1B4"
                  />
                  <rect
                    x="6"
                    y="13"
                    width="11"
                    height="1"
                    transform="rotate(-90 6 13)"
                    fill="#AFB1B4"
                  />
                </svg>
              </b-btn>
            </b-btn-group>
          </b-form-group>

          <template v-if="isActive.table()">
            <b-form-group>
              <div class="flex items-center gap-[13px]">
                <color-picker-placeholder
                  ref="colorCellPickerInvoker"
                  class="mr-2"
                  color="white"
                  @click.native="tableCellBgColor = {command: commands, name: 'background'}"
                />

                <label class="mb-0 pb-0">Cell background color</label>
              </div>
            </b-form-group>


            <div class="flex justify-between items-center w-full">
              <label
                class="mb-0 pb-0"
              >
                Cell Text Align
              </label>

              <b-form-group>
                <b-btn-group class="pt-[2px]">
                  <b-btn
                    variant="white"
                    class="w-[33px] h-[29px] flex items-center justify-center p-0"
                    @click="commands.alignment({ textAlign: 'left' })"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <rect
                        x="0.475586"
                        y="0.475403"
                        width="14.9508"
                        height="14.9508"
                        fill="url(#pattern0)"
                      />
                      <defs>
                        <pattern
                          id="pattern0"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            xlink:href="#image0_7663_56750"
                            transform="scale(0.00195312)"
                          />
                        </pattern>
                        <image
                          id="image0_7663_56750"
                          width="512"
                          height="512"
                          xlink:href="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>
                  </b-btn>
                  <b-btn
                    variant="white"
                    class="w-[33px] h-[29px] flex items-center justify-center p-0"
                    @click="commands.alignment({ textAlign: 'center' })"
                  >
                    <svg
                      width="18"
                      height="14"
                      viewBox="0 0 18 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <rect
                        x="0.951172"
                        y="0.431396"
                        width="16.5246"
                        height="13.0389"
                        fill="url(#pattern4)"
                      />
                      <defs>
                        <pattern
                          id="pattern4"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            xlink:href="#image0_7663_56749"
                            transform="scale(0.00195312 0.00247525)"
                          />
                        </pattern>
                        <image
                          id="image0_7663_56749"
                          width="512"
                          height="404"
                          xlink:href="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>
                  </b-btn>
                  <b-btn
                    variant="white"
                    class="w-[33px] h-[29px] flex items-center justify-center p-0"
                    @click="commands.alignment({ textAlign: 'right' })"
                  >
                    <svg
                      width="15"
                      height="16"
                      viewBox="0 0 15 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                    >
                      <rect
                        x="14.9512"
                        y="15.4262"
                        width="14.9508"
                        height="14.9508"
                        transform="rotate(-180 14.9512 15.4262)"
                        fill="url(#pattern0)"
                      />
                      <defs>
                        <pattern
                          id="pattern0"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            xlink:href="#image0_7663_56751"
                            transform="scale(0.00195312)"
                          />
                        </pattern>
                        <image
                          id="image0_7663_56751"
                          width="512"
                          height="512"
                          xlink:href="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>
                  </b-btn>
                </b-btn-group>
              </b-form-group>
            </div>


            <b-form-group>
              <div class="flex items-center justify-between">
                <label class="pb-0">Cell Border Type</label>

                <sid-select
                  :sid="`.--${section.sid}-text table :is(td, th)`"
                  rule-name="border-style"
                  :searchable="false"
                  class="w-[118px]"
                  :options="[{label: 'Solid', value: 'solid'}, {label: 'Dashed', value: 'dashed'}, {label: 'Dotted', value: 'dotted'}, {label: 'Double', value: 'double'}, {label: 'Outset', value: 'outset'}]"
                />
              </div>
            </b-form-group>

            <b-form-group>
              <sid-linked-group
                :sid="`.--${section.sid}-text table :is(td, th)`"
                label="Cell Border Width"
                rule-name="border"
                class="!mb-[7px]"
                :angles="[
                  {
                    text: '',
                    value: 'top-width'
                  },
                  {
                    text: '',
                    value: 'right-width'
                  },
                  {
                    text: '',
                    value: 'bottom-width'
                  },
                  {
                    text: '',
                    value: 'left-width'
                  }
                ]"
              />

              <div class="flex w-full justify-end">
                <sid-color
                  :sid="`.--${section.sid}-text table :is(td, th)`"
                  rule-name="border-color"
                />
              </div>
            </b-form-group>

            <sid-linked-group
              label="Cell Padding"
              :sid="`.--${section.sid}-text table :is(td, th)`"
              rule-name="padding"
              :min="0"
              :angles="[
                {
                  text: '',
                  value: 'top'
                },
                {
                  text: '',
                  value: 'right'
                },
                {
                  text: '',
                  value: 'bottom'
                },
                {
                  text: '',
                  value: 'left'
                }
              ]"
            />
          </template>
        </div>
      </editor-menu-bar>


      <sid-color
        v-show="false"
        ref="text-color"
        :sid="`.--${section.sid}-text div`"
        rule-name="color"
      />

      <sid-buttons-group
        v-show="false"
        ref="vertical-align"
        :options="[
          {
            value: 'top',
            tooltip: 'superscript'
          },
          {
            value: 'sub',
            tooltip: 'subscript'
          }
        ]"
        :sid="`.--${section.sid}-text div`"
        rule-name="vertical-align"
        @change="val => verticalAlign = val"
        @mount="val => verticalAlign = val"
      />

      <sid-buttons-group
        v-show="false"
        ref="text-transform"
        :options="[
          {
            value: 'uppercase',
            tooltip: 'uppercase'
          },
          {
            value: 'capitalize',
            tooltip: 'capitalize'
          },
          {
            value: 'lowercase',
            tooltip: 'lowercase'
          }
        ]"
        :sid="`.--${section.sid}-text div`"
        rule-name="text-transform"
        @change="val => textTransform = val"
        @mount="val => textTransform = val"
      />

      <sid-transform
        :sid="`.--${section.sid}-text div`"
        rule-name="transform"
        translate-dimensional="px"
        :section="section"
        class="mb-4"
        :controls="[
          {
            label: 'Skew',
            key: 'skew',
            min: -80,
            max: 80,
            step: 1,
            dimension: 'deg'
          },
          {
            label: 'Scale',
            key: 'scale',
            min: 0,
            max: 2,
            step: 0.1,
            dimension: ''
          },
          {
            label: 'Rotate',
            key: 'rotate',
            min: -360,
            max: 360,
            step: 1,
            dimension: 'deg'
          }
        ]"
      />
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      v-if="section.component === 'ElRichText'"
      title="Protect Content"
    >
      <b-btn
        v-if="!section.options.content.richText.protected"
        variant="primary"
        class="w-full"
        :class="{'opacity-80': loading}"
        :disabled="loading"
        @click="protectContent"
      >
        <i class="fa fa-lock mr-1 text-xs" />
        Protect Content
      </b-btn>

      <template v-else>
        <b-btn
          variant="primary"
          class="w-full"
          :class="{'opacity-80': loading}"
          :disabled="loading"
          @click="pushProtectionContent"
        >
          <i class="fa fa-lock mr-1 text-xs" />
          Update Content
        </b-btn>
        <b-btn
          variant="danger"
          class="w-full mt-2"
          :class="{'opacity-80': loading}"
          :disabled="loading"
          @click="removeProtectionContent"
        >
          <i class="fa fa-unlock mr-1 text-xs" />
          Unprotect
        </b-btn>
      </template>
      <p class="text-xs text-[#8A8A8A] leading-[18px] font-[400] font-[ProximaNova] mt-2 mb-[0]">
        Protect your content from unwanted access and prevent it from being copied or shared.
      </p>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      v-if="section.component !== 'ElRichText'"
      title="AI"
      beta
    >
      <chat-gpt
        :uid="section.uid"
        :text-editor="editor"
        @generated="insertText"
      />
    </SidbearSettingsGroup>

    <type-animation-settings
      v-if="!refresh && section.component !== 'ElRichText'"
      :editor="editor"
      :sid="section.sid"
    />

    <SidbearSettingsGroup title="Text Shadow">
      <text-shadow-control
        :config="config"
        class="!mb-[19px]"
      />
    </SidbearSettingsGroup>

    <template>
      <SidbearSettingsGroup
        v-if="!hide.includes('animations')"
        title="Animations"
      >
        <animations
          :sid="section.uid"
          :animations="animations"
        />
      </SidbearSettingsGroup>

      <SidbearSettingsGroup
        v-if="!hide.includes('spacing')"
        :title="`${groupPrefix} Padding & Margins`"
      >
        <sid-linked-group
          label="Padding"
          :sid="`.--${section.sid}-text`"
          rule-name="padding"
          :min="0"
          :angles="[
            {
              text: '',
              value: 'top'
            },
            {
              text: '',
              value: 'right'
            },
            {
              text: '',
              value: 'bottom'
            },
            {
              text: '',
              value: 'left'
            }
          ]"
        />

        <sid-linked-group
          label="Margin"
          :sid="`.--${section.sid}-margin`"
          rule-name="margin"
          :min="-2000"
          :max="2000"
          :angles="[
            {
              text: '',
              value: 'top'
            },
            {
              text: '',
              value: 'right'
            },
            {
              text: '',
              value: 'bottom'
            },
            {
              text: '',
              value: 'left'
            }
          ]"
        />
      </SidbearSettingsGroup>
    </template>
    <confirm-dialog
      ref="confirmDialog"
      :description="`You are about to change this ID, are you sure?`"
      @confirm="generateId()"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import {mapState} from 'vuex'
import Cookie from 'js-cookie'
import ClickOutside from '@/directives/click-outside'
import ModMixin from '@builder/components/mixins/ModMixin'
import Animations from '../controls/Animations'
import TiptapLink from '../controls/TiptapLink'
import ColorPickerFlat from '../utils/ColorPickerFlat'
import ColorPickerPlaceholder from '../utils/ColorPickerPlaceholder'
import TextShadowControl from '../controls/TextShadowControl'
import TiptapBackgroundColor from '../controls/TiptapBackgroundColor'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import GlobalStyleTextMixin from '@/components/mixins/GlobalStyleTextMixin'
import BackgroundType from '@/components/editor/controls/BackgroundType'
import SidFont from '@/components/editor/components/sid-controls/sid-control-pressets/font/SidFont'
import SidRangeSlider from '@/components/editor/components/sid-controls/SidRangeSlider'
import SidTransform from '@/components/editor/components/sid-controls/sid-control-pressets/transform/SidTransform'
import SidButtonsGroup from '@/components/editor/components/sid-controls/SidButtonsGroup'
import SidLinkedGroup from '@/components/editor/components/sid-controls/SidLinkedGroup'
import SidColor from '@/components/editor/components/sid-controls/SidColor'
import SidStyles from '@/components/mixins/SidStyles'
import TypeAnimationSettings from '@/components/editor/components/TypeAnimationSettings'
import ChatGpt from '@/components/editor/controls/ChatGpt'
import SidSelect from '@/components/editor/components/sid-controls/SidSelect.vue'
import ConfirmDialog from '@builder/components/editor/modals/ConfirmDialog'
import SidFontSize from '@/components/editor/components/sid-controls/sid-control-pressets/font/SidFontSize.vue'
import GlobalVariableContent from '@/components/editor/components/GlobalVariableContent.vue'
import GlobalVariableLink from '@/components/editor/components/GlobalVariableLink.vue'

// Tiptap v3-compatible EditorMenuBar (legacy API shim)
const EditorMenuBar = {
  name: 'EditorMenuBar',
  props: {
    editor: {
      type: Object,
      required: true
    },
    hide: {
      type: Array,
      default: () => []
    }
  },
  render () {
    const editor = this.editor

    if (!editor) {
      return null
    }

    // Old command names (v1) mapped to Tiptap v3 commands
    const explicitCommands = {
      bold: () => editor.chain().focus().toggleBold().run(),
      italic: () => editor.chain().focus().toggleItalic().run(),
      underline: () => editor.chain().focus().toggleUnderline().run(),
      strike: () => editor.chain().focus().toggleStrike().run(),
      bullet_list: () => editor.chain().focus().toggleBulletList().run(),
      ordered_list: () => editor.chain().focus().toggleOrderedList().run(),
      // v1 createTable -> v2/v3 insertTable
      createTable: (options) => editor.chain().focus().insertTable(options).run(),
      deleteTable: () => editor.chain().focus().deleteTable().run(),
      addColumnBefore: () => editor.chain().focus().addColumnBefore().run(),
      addColumnAfter: () => editor.chain().focus().addColumnAfter().run(),
      deleteColumn: () => editor.chain().focus().deleteColumn().run(),
      addRowBefore: () => editor.chain().focus().addRowBefore().run(),
      addRowAfter: () => editor.chain().focus().addRowAfter().run(),
      deleteRow: () => editor.chain().focus().deleteRow().run(),
      // v1 toggleCellMerge helper -> v3 mergeOrSplit
      toggleCellMerge: () => editor.chain().focus().mergeOrSplit().run(),
      // simple link helper so commands.link(attrs) still works
      link: (attrs) => editor.chain().focus().extendMarkRange('link').setLink(attrs).run()
    }

    const baseCommands = editor.commands || {}

    const commands = new Proxy(explicitCommands, {
      get (target, key) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          return target[key]
        }

        const commandFn = baseCommands[key]

        if (typeof commandFn === 'function') {
          // For custom extensions we just call the command directly
          return (...args) => commandFn(...args)
        }

        // Fallback: try to use chain API directly if available
        return (...args) => {
          const chain = editor.chain().focus()
          if (typeof chain[key] === 'function') {
            return chain[key](...args).run()
          }
          if (process && process.env && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[EditorMenuBar] Unknown command:', key)
          }
          return null
        }
      }
    })

    const activeNameMap = {
      bullet_list: 'bulletList',
      ordered_list: 'orderedList'
    }

    const isActive = new Proxy({}, {
      get (_, key) {
        const typeName = activeNameMap[key] || key
        return (attrs) => editor.isActive(typeName, attrs)
      }
    })

    const getMarkAttrs = (name) => {
      try {
        return editor.getAttributes(name) || {}
      } catch (e) {
        return {}
      }
    }

    const slot = this.$scopedSlots.default

    if (!slot) {
      return null
    }

    const children = slot({
      editor,
      commands,
      isActive,
      getMarkAttrs
    })

    return Array.isArray(children) ? children[0] : children
  }
}

export default {
  name: 'TiptapMod',

  directives: {ClickOutside},

  components: {
    GlobalVariableLink,
    GlobalVariableContent,
    ConfirmDialog,
    SidFontSize,
    SidSelect,
    ChatGpt,
    TypeAnimationSettings,
    SidColor,
    SidLinkedGroup,
    SidButtonsGroup,
    SidTransform,
    SidRangeSlider,
    SidFont,
    BackgroundType,
    SidbearSettingsGroup,
    TiptapBackgroundColor,
    TextShadowControl,
    ColorPickerPlaceholder,
    ColorPickerFlat,
    TiptapLink,
    Animations,
    EditorMenuBar
  },

  mixins: [
    ModMixin,
    GlobalStyleTextMixin,
    SidStyles
  ],

  data () {
    return {
      loading: false,
      refresh: false,
      tipa: null,
      backgroundMark: {
        color: null
      },
      tableCellBgColor: null,
      verticalAlign: '',
      textTransform: '',
      backgroundColorTip: null,
      pickedColor: '',
      localFontWeight: null,
      backgroundColor: '',
      bgEffect: 'Select effect',
      linkMenuIsActive: false,
      linkAttrs: {},
      linkSaveText: 'Save changes',
      linkDeleteText: 'Delete link',
      globalVariableActive: false,
      colorType: {
        backgroundType: null
      },
      backgroundGradient: {
        backgroundGradient: [
          {
            'color': 'rgb(119,171,255)',
            'position': 0
          },
          {
            'color': 'rgb(0,60,152)',
            'position': 100
          }
        ]
      },
      headlineTags: [
        { label: 'H1', value: 'h1' },
        { label: 'H2', value: 'h2' },
        { label: 'H3', value: 'h3' },
        { label: 'H4', value: 'h4' },
        { label: 'H5', value: 'h5' },
        { label: 'H6', value: 'h6' }
      ],
      typographyTags: [
        { label: 'H1', value: 'h1' },
        { label: 'H2', value: 'h2' },
        { label: 'H3', value: 'h3' },
        { label: 'H4', value: 'h4' },
        { label: 'H5', value: 'h5' },
        { label: 'H6', value: 'h6' },
        { label: 'Paragraph', value: 'p' }
      ]
    }
  },

  computed: {
    textUid () {
      return this.section.uid
    },
    gradientColor: {
      get () {
        return _.get(this.config, ['gradientColor', this.resolutioner], _.get(this.config, ['gradientColor', 'lg'], false))
      },
      set (val) {
        this.$set(this.config, 'gradientColor', {..._.set(this.config['gradientColor'] || {}, [this.resolutioner], val)})
      }
    },
    animations: {
      get () {
        if (!Object.prototype.hasOwnProperty.call(this.config, 'animations')) {
          this.$set(this.config, 'animations', {
            animation: null,
            once: true,
            offset: 200,
            delay: 50
          })
        }

        return this.config.animations
      },
      set (val) {
        this.config.animations = val
      }
    },
    ...mapState('editor', {
      editor: state => state.tiptap,
      openedItem: state => state.openedItem
    }),
    headlineTag: {
      get () {
        return this.config.tag || 'h2'
      },
      set (val) {
        this.$set(this.config, 'tag', val)
      }
    },
    typographyTag: {
      get () {
        return this.config.tag || 'p'
      },
      set (val) {
        this.$set(this.config, 'tag', val)
      }
    }
  },

  watch: {
    textUid () {
      this.refresh = true
      setTimeout(() => this.refresh = false, 100)
    },
    openedItem (val) {
      if (val === this.componentName) {
        VEvent.fire(this.section.uid + '_' + this.componentName)
      }
    },
    fontFamily () {
      this.config.resolutionStyle.lg.fontWeight = 400
    }
  },

  mounted () {
    VEvent.listen('tiptap-mod-color', () => {
      this.linkMenuIsActive = false
    })
    VEvent.listen('tiptap-mod-font-weight', fontWeight => this.localFontWeight = fontWeight)

    document.addEventListener('mousedown', this.onMouseDown, true)
    window.addEventListener('highlighter-click', this.onSelectionElement)
  },

  destroyed () {
    document.removeEventListener('mousedown', this.onMouseDown, true)
    window.removeEventListener('highlighter-click', this.onSelectionElement)
  },

  methods: {
    getBlockType () {
      if (!this.editor) return 'p'
      const { state } = this.editor
      const $pos = state.selection.$from
      // Walk up the tree to find the nearest block node (paragraph or heading)
      for (let d = $pos.depth; d >= 0; d--) {
        const node = $pos.node(d)
        if (!node) continue
        const name = node.type && node.type.name
        if (name === 'heading') {
          const level = node.attrs && node.attrs.level ? node.attrs.level : 1
          return `h${level}`
        }
        if (name === 'paragraph') return 'p'
      }
      return 'p'
    },
    async protectContent () {
      this.loading = true
      const communityInstance  = axios.create({
        baseURL: 'https://community.estage.com',
        headers: {
          'Estage-Authorization': Cookie.get('cross_estage_token')
        }
      })

      await communityInstance.post(`/api/${this.project.id}/legal`, {
        html: this.section.options.content.richText.text
      })
        .then(async ({data}) => {
          this.$set(this.section.options.content.richText, 'protected', data.page)
          await this.$store.dispatch('projects/save')
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Content has been successfully unprotected',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async pushProtectionContent () {
      this.loading = true
      const communityInstance  = axios.create({
        baseURL: 'https://community.estage.com',
        headers: {
          'Estage-Authorization': Cookie.get('cross_estage_token')
        }
      })

      await communityInstance.put(`api/${this.project.id}/legal/${this.section.options.content.richText.protected}`, {
        html: this.section.options.content.richText.text
      })
        .then(() => {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Content has been successfully updated',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async removeProtectionContent () {
      this.loading = true
      const communityInstance  = axios.create({
        baseURL: 'https://community.estage.com',
        headers: {
          'Estage-Authorization': Cookie.get('cross_estage_token')
        }
      })
      
      await communityInstance.delete(`api/${this.project.id}/legal/${this.section.options.content.richText.protected}`)
        .then(() => {
          this.$set(this.section.options.content.richText, 'protected', false)
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Content has been successfully unprotected',
            showConfirmButton: false,
            timer: 3500
          })
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setBlockType (type, commands) {
      if (type === 'p' || type === 'paragraph') {
        if (typeof commands.paragraph === 'function') return setTimeout(() => commands.paragraph(), 100)
      } else if (type === 'h1') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 1 }), 100)
      } else if (type === 'h2') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 2 }), 100)
      } else if (type === 'h3') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 3 }), 100)
      } else if (type === 'h4') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 4 }), 100)
      } else if (type === 'h5') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 5 }), 100)
      } else if (type === 'h6') {
        if (typeof commands.heading === 'function') return setTimeout(() => commands.heading({ level: 6 }), 100)
      }
    },
    isGlobalVarActive () {
      const { state } = this.editor
      const markType = this.editor.schema.marks.globalVariable
      const { from, $from, to, empty } = state.selection

      if (empty) {
        return !!markType.isInSet(state.storedMarks || $from.marks())
      } else {
        return state.doc.rangeHasMark(from, to, markType)
      }
    },
    lol () {
      const { selection } = this.editor.state
      const { $from } = selection

      if ($from.node().type.spec.tableRole === 'cell') {
        const cellPos = $from.start($from.depth)
        const fontSize = 1 // Change this to your desired font size

        const attrs = Object.assign({}, $from.node().attrs)

        // Set the font size attribute
        attrs.style = `font-size: ${fontSize}px`

        const tr = this.editor.state.tr
        tr.setNodeMarkup(cellPos, null, attrs)

        // Update the cell's attributes
        // this.editor.state.doc = this.editor.state.doc.updateCell(cellPos, null, attrs)

        // Replace the current editor state with the updated transaction
        this.editor.view.dispatch(tr)
      }
    },
    insertText (text) {
      this.editor.view.dom.textContent = text
    },
    onSelectionElement (e) {
      const target = e && e.detail
      if (!target || !target.closest) return

      const _targetDom = target.closest('.ProseMirror')

      if (_targetDom) {
        if (this.editor && this.editor.view && this.editor.view.dom !== _targetDom) {
          try {
            if (this.editor.commands && typeof this.editor.commands.blur === 'function') {
              this.editor.commands.blur()
            }
          } catch (err) {
            console.error(err)
          }

          try {
            window.getSelection()?.removeAllRanges()
          } catch (err) {
            // ignore
          }

          try {
            this.$store.commit('editor/SET_TIPTAP', null)
          } catch (err) {
            // ignore
          }
        }
      }
    },
    textColorTypeChanged (commands, getMarkAttrs, val) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (val.value) {
        const raw = getMarkAttrs('textgradientcolor').raw
        const rawParsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
        const hasRaw = Array.isArray(rawParsed) && rawParsed.length > 0

        if (!empty && !hasRaw) {
          this.applyGradientTextColor(this.backgroundGradient, commands)
        }

        const configGradient = this.config.resolutionStyle[this.resolutioner].backgroundGradient
        const hasConfigGradient = Array.isArray(configGradient) && configGradient.length > 0

        if (empty && !hasConfigGradient) {
          this.applyGradientTextColor(this.backgroundGradient, commands)
        }
      } else {
        if (empty) {
          this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradient', null)
          this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradientDirection', null)
          commands.textgradientcolor(null)
        } else {
          if (getMarkAttrs('textgradientcolor').raw) {
            commands.textgradientcolor(null)
          } else {
            this.applyBackgroundColor(val, commands)
          }
        }
      }
    },
    onMouseDown (e) {
      if (e.target.closest('.color-picker-flat') || e.target.closest('.pollite') || e.target.closest('.vc-sketch') || e.target.closest('.v-select') || e.target.closest('.vs__dropdown-menu')) return
      this.backgroundMark.color = null

      this.tipa = null
      this.tableCellBgColor = null
      this.backgroundColorTip = null
    },
    applyGlobalVariable (commands, val) {
      setTimeout(() => commands.globalVariable({variable: val}), 100)
    },
    applyGlobalVariableLink (commands, val) {
      setTimeout(() => commands.globalVariableLink({variable: val}), 100)
    },
    applyFontFamily (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.fontFamily || !empty) {
        return setTimeout(() => commands.fontfamily({fontFamily: val.val}), 100)
      }

      val.apply()
    },
    applyFontWeight (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.fontWeight || !empty) {
        return setTimeout(() => commands.fontweight({fontWeight: val.val}), 100)
      }

      val.apply()
    },
    applyFontSize (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.sid || !empty) {
        commands.setFontSize({sid: val.sid})
        setTimeout(() => val.apply(), 100)
        return
      }

      val.apply()
    },
    resetFont (commands) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (!empty) {
        setTimeout(() => commands.resetFont(), 100)
      }
    },
    resetFontSize (val, commands, selected) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (selected.sid || !empty) {
        val.reset()
        setTimeout(() => commands.removeFontSize(), 100)
        return
      }

      val.reset()
    },
    setCommands (commands) {
      this.tipa = commands
    },
    setBgCommands (commands) {
      this.backgroundColorTip = commands
    },
    applyTextColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty
      this.pickedColor = val

      if (empty) {
        this.$refs['text-color'].val = val
      } else {
        commands.textcolor({color: val})
      }
    },
    applyGradientTextColor ({backgroundGradient, gradientDirection}, commands, selection = false) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (empty && !selection) {
        this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradient', backgroundGradient)
        this.$set(this.config.resolutionStyle[this.resolutioner], 'backgroundGradientDirection', gradientDirection)
      } else {
        commands.textgradientcolor({backgroundGradient, gradientDirection})
        window.getSelection()?.removeAllRanges()
      }
    },
    applyBackgroundColor (val, commands) {
      const {state} = this.editor
      const empty = state.selection.empty

      if (!empty) {
        commands.backgroundColor({backgroundColor: val})
      }
    },
    applyTextBackground (val, commands) {
      if (!val) {
        return commands.background()
      }

      commands.background({class: 'asd', background: `url("${val}") no-repeat 0 100%`})
    },
    getLinkAttributes (attrs) {
      const _attrs = attrs.dataVueHref ? JSON.parse(attrs.dataVueHref) : attrs

      return {
        href: _attrs.href || {},
        target: _attrs.target,
        file: _attrs.file,
        styleeffect: attrs.styleeffect,
        fromGlobalStyle: attrs.fromGlobalStyle,
        id: attrs.id
      }
    },
    setLinkUrl (command, url) {
      setTimeout(() => {
        command({
          href: url.href.slug || url.href.href,
          target: url.target || '_self',
          dataVueHref: JSON.stringify({href: url.href, target: url.target, file: url.file}),
          fromGlobalStyle: typeof this.linkAttrs.fromGlobalStyle === 'string' ? this.linkAttrs.fromGlobalStyle === 'true' : this.linkAttrs.fromGlobalStyle,
          styleeffect: this.linkAttrs.styleeffect,
          id: url.id
        })
        this.linkSaveText = 'Saved!'

        setTimeout(() => this.linkSaveText = 'Save changes', 800)
      }, 100)
    },
    deleteLinkUrl (commands) {
      this.setLinkUrl(commands.link, {
        href: '', target: '__self', style: null
      })
      this.linkDeleteText = 'Deleted!'
      this.linkMenuIsActive = false
      setTimeout(() => this.linkDeleteText = 'Delete link', 800)
    },
    getPickedColorAttributes (attrs) {
      this.pickedColor = attrs.color || window.getComputedStyle(this.editor.view.dom).color
      return this.pickedColor
    },
    getPickedGradientColorAttributes (attrs) {
      const bgGradient = this.config.resolutionStyle[this.resolutioner].hasOwnProperty('backgroundGradient') ? this.config.resolutionStyle[this.resolutioner].backgroundGradient : this.config.resolutionStyle['lg'].backgroundGradient
      const rawParsed = attrs.raw ? (typeof attrs.raw === 'string' ? JSON.parse(attrs.raw) : attrs.raw) : null
      const hasRaw = Array.isArray(rawParsed) && rawParsed.length > 0
      const hasBgGradient = Array.isArray(bgGradient) && bgGradient.length > 0
      this.colorType.backgroundType = hasRaw || hasBgGradient ? 'gradient' : null

      if (!hasRaw && this.config.resolutionStyle[this.resolutioner].backgroundGradient) {
        this.backgroundGradient.backgroundGradient = this.config.resolutionStyle[this.resolutioner].backgroundGradient
      }

      return JSON.stringify(attrs)
    },
    getPickedBackgroundColorAttributes (attrs) {
      this.backgroundColor = attrs.backgroundColor || '#fff'
      return this.backgroundColor
    },
    generateId (){
      this.$set(this.config, 'id', Math.random().toString(36).substr(2, 9))
    }
  }
}
</script>

<style lang="scss" scoped>
.menubar {
  .btn-menubar-button {
    border: 1px solid #E2E5EC;
    background-color: #fff;

    i {
      color: #aeb1b4;
    }

    &.is-active i {
      color: #44474A;
    }
  }
}
</style>
